import { c } from "react/compiler-runtime";
import { makeValueFormatter } from "@effektify/components";
import useLogger from "./useLogger.js";
import useReportTranslations from "./useReportTranslations.js";
import { sprintf } from "sprintf-js";
const isBasicChartConfigProps = props => {
  if (typeof props !== "object" || props === null) return false;
  if ("columns" in props) return false;
  const {
    configuration,
    series
  } = props;
  return typeof configuration === "object" && configuration !== null && typeof configuration.x === "object" && configuration.x !== null && typeof configuration.x.data === "object" && configuration.x.data !== null && Array.isArray(series);
};
const isSpreadStackedBarChartConfigProps = props => {
  if (typeof props !== "object" || props === null) return false;
  if (!("columns" in props)) return false;
  if (!("configuration" in props)) return false;
  if (!("series" in props)) return false;
  return true;
};
const isChartWithConfig = chartConfig => {
  if (typeof chartConfig !== "object" || chartConfig === null) return false;
  if (!("configuration" in chartConfig)) return false;
  if (!("type" in chartConfig)) return false;
  return chartConfig.type === "bar_chart" || chartConfig.type === "line_chart" || chartConfig.type === "semi-circle";
};
const getXAxisKey = chartConfig => isChartWithConfig(chartConfig) ? chartConfig.configuration.x.data.key.split(".").pop() || "" : "";
const getXAxisTrKey = chartConfig => isChartWithConfig(chartConfig) ? chartConfig.configuration.x.label?.key || "" : "";
const getSeriesDataKey = series => series.data.key.split(".").pop() || "";
const formatLabel = (value, format) => {
  if (!format) return `${value}`;
  try {
    return sprintf(format, value);
  } catch {
    return `${value}`;
  }
};
const getXAxisDetailsFn = (seriesData, chartConfig, columns, t, logger) => {
  const xAxisKey = getXAxisKey(chartConfig);
  const xAxisTrKey = getXAxisTrKey(chartConfig);
  const values = seriesData[xAxisKey] || [];
  if (!values || values.length === 0) {
    logger.debug("No x-axis data found for chart", chartConfig, seriesData);
    return [];
  }
  if (!xAxisKey) {
    logger.debug("No x-axis key found in chart configuration", chartConfig);
    return [];
  }
  const xAxisColumn = columns.find(col => col.key === xAxisKey);
  return [{
    label: t(xAxisTrKey),
    id: xAxisKey,
    tickBorderStroke: "transparent",
    grid: {
      stroke: "transparent"
    },
    data: values.map(value => `${value}`) || [],
    // TODO: Handle conversion to Date if needed
    valueFormatter: date => formatLabel(date, xAxisColumn?.label_format),
    type: "point",
    scaleType: "point"
  }];
};
const getSeriesWithDataFn = (seriesData, seriesConfig) => seriesConfig.keys.map(key => ({
  ...seriesConfig.details[key],
  data: seriesData[key]
}));
const useDatasetToSeries = () => {
  const $ = c(9);
  const {
    translate: t
  } = useReportTranslations();
  const logger = useLogger("useDatasetToSeries");
  let t0;
  if ($[0] !== logger || $[1] !== t) {
    t0 = (dataset, chartConfig) => {
      let seriesData = {};
      let seriesConfig = {
        keys: [],
        details: {}
      };
      if (isBasicChartConfigProps(chartConfig)) {
        const result = resolveSeriesDataFn(dataset, chartConfig, t);
        seriesData = result.seriesData;
        seriesConfig = result.seriesConfig;
      } else {
        if (isSpreadStackedBarChartConfigProps(chartConfig)) {
          const result_0 = resolveRowedDataToSeries(dataset, chartConfig, t);
          seriesData = result_0.seriesData;
          seriesConfig = result_0.seriesConfig;
        }
      }
      return {
        xAxis: getXAxisDetailsFn(seriesData, chartConfig, dataset.columns, t, logger),
        series: getSeriesWithDataFn(seriesData, seriesConfig),
        seriesData
      };
    };
    $[0] = logger;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const resolveSeriesData = t0;
  let t1;
  if ($[3] !== logger || $[4] !== t) {
    t1 = (seriesData_0, chartConfig_0, columns) => getXAxisDetailsFn(seriesData_0, chartConfig_0, columns, t, logger);
    $[3] = logger;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getXAxisDetails = t1;
  let t2;
  if ($[6] !== getXAxisDetails || $[7] !== resolveSeriesData) {
    t2 = {
      resolveSeriesData,
      getXAxisDetails,
      getSeriesWithData: getSeriesWithDataFn
    };
    $[6] = getXAxisDetails;
    $[7] = resolveSeriesData;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const resolveSeriesDataFn = (dataset, {
  type: chartType,
  series
}, t) => {
  const mappedData = {};
  const keys = [];
  const details = {};
  const {
    columns,
    data
  } = dataset;
  data.forEach(row => {
    row.forEach((value, index) => {
      const key = columns[index]?.key;
      if (!key) return;
      if (!mappedData[key]) mappedData[key] = [];
      mappedData[key].push(value === "" ? null : value);
    });
  });
  series.forEach((seriesItem, index) => {
    const key = getSeriesDataKey(seriesItem);
    const colIndex = columns.findIndex(col => col.key === key);
    if (colIndex === -1) return;
    keys.push(key);
    const seriesType = series[index]?.type;
    const valueFormatter = makeValueFormatter(columns[colIndex]);
    const axisValueFormatter = makeValueFormatter(columns[colIndex], 0);
    details[key] = {
      id: key,
      type: seriesType === "line" || seriesType === "area" || chartType === "line_chart" ? "line" : "bar",
      label: typeof seriesItem.label === "string" ? seriesItem.label : t(seriesItem.label?.key),
      showMark: false,
      area: seriesType === "area",
      ...(valueFormatter ? {
        valueFormatter
      } : {}),
      ...(axisValueFormatter ? {
        axisValueFormatter
      } : {}),
      ...(seriesItem["y-axis"] !== void 0 ? {
        yAxisId: seriesItem["y-axis"]
      } : {})
    };
  });
  return {
    seriesData: mappedData,
    seriesConfig: {
      keys,
      details
    }
  };
};
const resolveRowedDataToSeries = ({
  columns,
  data
}, chartConfig, t) => {
  const {
    band,
    series,
    data: value
  } = chartConfig.columns;
  const bandIdx = columns.findIndex(col => col.key === band.key);
  const seriesIdx = columns.findIndex(col => col.key === series.key);
  const valueIdx = columns.findIndex(col => col.key === value.key);
  if ([bandIdx, seriesIdx, valueIdx].some(idx => idx === -1)) {
    throw new Error("One or more required column keys are missing in the dataset.");
  }
  const seriesMap = {};
  const bands = /* @__PURE__ */new Set();
  for (const row of data) {
    const bandKey = row[bandIdx];
    const seriesKey = row[seriesIdx];
    const rawValue = row[valueIdx];
    const numericValue = typeof rawValue === "number" ? rawValue : Number(rawValue ?? 0);
    bands.add(bandKey);
    if (!seriesMap[seriesKey]) {
      seriesMap[seriesKey] = {
        label: t(seriesKey),
        data: {},
        stack: "total",
        type: "bar"
      };
    }
    seriesMap[seriesKey].data[bandKey] = numericValue;
  }
  const bandOrder = Array.from(bands);
  const seriesData = {};
  const details = {};
  const keys = [];
  for (const [key, def] of Object.entries(seriesMap)) {
    keys.push(key);
    seriesData[key] = bandOrder.map(band2 => def.data[band2] ?? 0);
    details[key] = def;
  }
  return {
    seriesData,
    seriesConfig: {
      keys,
      details
    }
  };
};
export { useDatasetToSeries as default, isBasicChartConfigProps, resolveRowedDataToSeries, resolveSeriesDataFn, useDatasetToSeries };